import styled from 'styled-components';
import {Avatar, Detail, Label, OutlinedPillVariant, Pill, Text} from '@forecast-it/design-system';
import {sanitizeMessageHtml} from '../utils/sanitizeMessageHtml';
import {rewriteInlineImageSrcs} from '../utils/rewriteInlineImageSrcs';
import {relativeTime} from '../utils/relativeTime';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

export type StreamEntryType = 'email-sent' | 'email-received' | 'internal-note';

export interface StreamEntry {
	id: string;
	type: StreamEntryType;
	actorName: string;
	actorInitial: string;
	body: string;
	bodyHtml?: string | null;
	createdAt: string;
}

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const AvatarSlot = styled.div`
	flex: none;
`;
const Main = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
	min-width: 0;
	flex: 1 1 0%;
`;
const Header = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
// The body sits directly under the name with no card around it: a conversation is a
// sequence of people speaking, and a border per turn makes it read as a list of
// records instead.
const Body = styled.div`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	white-space: pre-wrap;
	min-width: 0;
	overflow-wrap: break-word;
`;

/**
 * Who is speaking, where a reader needs to know.
 *
 * The customer is marked because a reply leaves the building. An internal note is
 * marked because it must not — it looks exactly like a reply otherwise, and that is
 * the one confusion in this view with a real cost.
 */
function RoleTag({type}: {type: StreamEntryType}) {
	if (type === 'email-received') {
		return (
			<Pill outlined outlinedVariant={OutlinedPillVariant.ACTIVE}>
				Customer
			</Pill>
		);
	}
	if (type === 'internal-note') {
		return (
			<Pill outlined outlinedVariant={OutlinedPillVariant.NEUTRAL}>
				Internal note
			</Pill>
		);
	}
	return null;
}

interface Props {
	entry: StreamEntry;
}

/** One turn in the conversation: who said it, when, and what they said. */
export function StreamHistoryCard({entry}: Props) {
	return (
		<Row data-testid="stream-history-card">
			<AvatarSlot>
				<Avatar name={entry.actorName} size="m" />
			</AvatarSlot>
			<Main>
				<Header>
					<Label>{entry.actorName}</Label>
					<RoleTag type={entry.type} />
					<Detail color="medium" data-testid="stream-card-timestamp">
						{relativeTime(entry.createdAt)}
					</Detail>
				</Header>
				<Body>
					{entry.bodyHtml != null ? (
						<div
							dangerouslySetInnerHTML={{
								__html: rewriteInlineImageSrcs(
									sanitizeMessageHtml(entry.bodyHtml),
									ticketingAttachmentDownloadUrl
								),
							}}
						/>
					) : (
						<Text size="2">{entry.body}</Text>
					)}
				</Body>
			</Main>
		</Row>
	);
}
